import React from "react";
import { useCallback } from "react";
import { notify } from "../../../../shared/context/AlertContext";
import { CommonService } from "../../../../services/Common";
import { LoanApplicationService } from "../../../../services/LoanApplication";

export const useDocumentApiService = () => {
  const uploadDocument = useCallback(
    async ({
      file,
      userID,
      mediaType,
      pageName,
      token,
      isLenderPlatform,
    }: {
      file: File;
      userID: string;
      token: string;
      mediaType: string;
      pageName: string;
      isLenderPlatform: boolean;
    }) => {
      const formData = new FormData();
      formData.append("userID", userID);
      formData.append("mediaType", mediaType);
      formData.append("file", file);

      try {
        const response = await CommonService.uploadDashboardMedia({
          formData,
          token: token,
          pageName,
          isLenderPlatform,
        });

        if (response.status) {
          return response;
        } else {
          notify({
            title: "Upload Error",
            text: response.error,
          });
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  const onUpdateAdditionalKYCDoc = useCallback(
    async ({
      documentID,
      status,
      loanApplicationID,
      token,
      pageName,
    }: {
      documentID: string;
      status: number;
      loanApplicationID: string;
      token: string;
      pageName: string;
    }) => {
      const formData = {
        documentID,
        loanApplicationID,
        status,
      };

      try {
        const response = await LoanApplicationService.updateAdditionalDoc({
          formData,
          token,
          pageName,
        });

        if (response.status) {
          return response;
        } else {
          notify({
            title: "Error",
            text: response.error,
          });
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  const updateCoApplicantKYCDocument = useCallback(
    async ({
      loanApplicationID,
      userID,
      coApplicantKYCDetailsID,
      isBack,
      mediaID,
      token,
      pageName,
    }: {
      loanApplicationID: string;
      userID: string;
      coApplicantKYCDetailsID: string;
      isBack: boolean;
      mediaID: string;
      token: string;
      pageName: string;
    }) => {
      const payload = {
        loanApplicationID,
        userID,
        docs: [
          {
            frontMediaID: !isBack ? mediaID : "",
            backMediaID: isBack ? mediaID : "",
            id: coApplicantKYCDetailsID,
          },
        ],
      };

      try {
        const response = await LoanApplicationService.updateCoApplicantKYCDoc({
          payload,
          token,
          pageName,
        });

        if (response.status) {
          return response;
        } else {
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  const updateKYCDocument = useCallback(
    async ({
      changes,
      isBack,
      loanApplicationID,
      loanKYCDetailsID,
      mediaID,
      token,
      pageName,
      isLenderDashboard,
    }: {
      changes: string[];
      isBack: boolean;
      loanApplicationID: string;
      loanKYCDetailsID: string;
      mediaID: string;
      token: string;
      pageName: string;
      isLenderDashboard: boolean;
    }) => {
      const formData = {
        changes,
        isBack,
        loanApplicationID,
        loanKYCDetailsID,
        mediaID,
      };

      try {
        const response = await LoanApplicationService.updateKYCDoc({
          formData,
          token,
          pageName,
          isLenderDashboard,
        });

        if (response.status) {
          return response;
        } else {
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  const updateAdditionalKYCDocumentMedia = useCallback(
    async ({
      documentID,
      loanApplicationID,
      mediaIDs,
      token,
      pageName,
    }: {
      documentID: string;
      loanApplicationID: string;
      mediaIDs: string[];
      token: string;
      pageName: string;
    }) => {
      const formData = {
        documentID,
        loanApplicationID,
        mediaIDs,
      };

      try {
        const response = await LoanApplicationService.updateAdditionalKYCDoc({
          formData,
          token,
          pageName,
        });

        if (response.status) {
          return response;
        } else {
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  const updateCoApplicantDocReviewStatus = useCallback(
    async ({
      coApplicantKYCDetailsID,
      loanApplicationID,
      status,
      token,
      pageName,
    }: {
      coApplicantKYCDetailsID: string;
      loanApplicationID: string;
      status: number;
      token: string;
      pageName: string;
    }) => {
      const formData = {
        coApplicantKYCDetailsID,
        loanApplicationID,
        reviewStatus: status,
      };

      try {
        const response =
          await LoanApplicationService.updateCoApplicantDocReviewStatus({
            formData,
            token,
            pageName,
          });

        if (response.status) {
          notify({
            title: "Success",
            type: "success",
            text: "Status updated successfully!",
          });

          setTimeout(() => {
            window.location.reload();
          }, 1500);

          return response;
        } else {
          notify({
            title: "Error",
            text: response.error,
          });
          return null;
        }
      } catch (error: any) {
        return null;
      }
    },
    [notify]
  );

  // Add more document-related APIs here as needed

  return {
    uploadDocument,
    onUpdateAdditionalKYCDoc,
    updateKYCDocument,
    updateCoApplicantKYCDocument,
    updateAdditionalKYCDocumentMedia,
    updateCoApplicantDocReviewStatus,
    // otherAPI,
  };
};
